'use client';
import { useContext, useMemo } from 'react';
import { CmsClientContext } from '../utils/cmsPageContext/CmsClientContext';
import {
  type CalculatedAttributeResolver,
  createCalculatedAttributeResolver,
} from './resolve';
import { useScopeCalculations } from './store';

/**
 * Store-fed facade over the engine, in the `useCtaResolver` idiom: hooks
 * cannot run inside render loops, so this builds the resolver for the block's
 * slot once per render and returns it, callable per cell/row.
 *
 * This is how blocks are meant to consume the engine: a block never builds
 * sources by hand, so it cannot forget one. Non-React surfaces (the product
 * schema) build the same resolver with `createCalculatedAttributeResolver`.
 */
export function useCalculatedAttributeResolver(): CalculatedAttributeResolver {
  const { vertical, travelQuote, personalLoan } = useScopeCalculations();
  // Optional on purpose: a block in a story or test may render outside the
  // client provider, and a price without a currency prefix beats a throw.
  const context = useContext(CmsClientContext);
  const currency = context?.currencyDisplay;
  const premiums = travelQuote?.premiums;
  // Fed in, not read by blocks: the loader-or-dash answer is the engine's.
  const status = travelQuote?.status;
  const calculations = personalLoan?.calculations;
  const pending = personalLoan?.pending;

  return useMemo(
    () =>
      createCalculatedAttributeResolver(
        { vertical, travelQuote, personalLoan },
        { currency },
      ),
    // The parts the resolver reads, not the slot objects: the store is rebuilt
    // on every recalculation, and a block memoising on the resolver should not
    // re-resolve for nothing.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [vertical, premiums, status, calculations, pending, currency],
  );
}
